<template>
 <section class="userperms-page">
  <header-page :title="$route.meta.title || 'Permisos del usuario'" />
  <div class="userperms-content">
   <v-card class="userperms-card" flat>
    <div v-if="saving || (loading && ready)" class="userperms-overlay">
     <v-progress-circular indeterminate color="primary" :size="46" :width="4" />
     <span>{{ saving ? 'Guardando personalización…' : 'Cargando…' }}</span>
    </div>

    <v-card-title class="userperms-heading">
     <div class="userperms-title-group">
      <span class="userperms-title-icon"><v-icon aria-hidden="true">manage_accounts</v-icon></span>
      <div>
       <span class="userperms-eyebrow">ADMINISTRACIÓN · PERMISOS POR USUARIO</span>
       <h1>{{ target.userName || 'Permisos del usuario' }}</h1>
       <div v-if="ready" class="userperms-sub">
        <span class="userperms-chip-role"><v-icon small aria-hidden="true">badge</v-icon>{{ roleName }}</span>
        <span v-if="totalOverrides > 0" class="userperms-chip-custom">
         <v-icon small aria-hidden="true">tune</v-icon>{{ totalOverrides }} personalización(es)
        </span>
        <span v-else class="userperms-chip-standard">
         <v-icon small aria-hidden="true">verified</v-icon>Igual al estándar del rol
        </span>
       </div>
      </div>
     </div>
     <v-spacer />
     <v-btn
      outline class="userperms-back-action" color="secondary"
      :to="{ name: 'users' }" :disabled="saving">
      <v-icon small left aria-hidden="true">arrow_back</v-icon>Volver a usuarios
     </v-btn>
     <app-button
      green text="Guardar personalización" width="220" height="38"
      :loading="saving" :disabled="saving || !ready || !dirty"
      @click="askSave" />
    </v-card-title>

    <v-card-text>
     <v-alert :value="!!error" type="error">{{ error }}</v-alert>
     <v-alert :value="!!message" type="success">{{ message }}</v-alert>
     <v-progress-linear v-if="loading && !ready" indeterminate />

     <template v-if="allowed && ready">
      <p class="userperms-intro">
       Parte del <strong>estándar del rol</strong>. Marca o desmarca para
       <strong>personalizar</strong> este usuario. Lo que difiera del rol se marca como
       <span class="userperms-tag-inline">personalizado</span>. <strong>R (Ver)</strong> es la
       acción maestra: sin ella el recurso queda sin acceso.
      </p>

      <div class="userperms-toolbar">
       <div class="userperms-search">
        <v-text-field
         v-model="filterText" hide-details clearable
         prepend-inner-icon="search" label="Buscar módulo o recurso" />
       </div>
       <div class="userperms-bulk">
        <v-tooltip top>
         <app-button
          slot="activator" gray :text="allCollapsed ? 'Expandir todo' : 'Contraer todo'"
          width="130" height="34" @click="toggleAllCollapse" />
         <span>Abre o cierra todas las tarjetas de módulos.</span>
        </v-tooltip>
        <v-tooltip top>
         <app-button
          slot="activator" blue text="Volver al estándar" width="150" height="34"
          :disabled="saving || totalOverrides === 0" @click="resetAll" />
         <span>Descarta TODA la personalización y deja al usuario igual que el rol.</span>
        </v-tooltip>
       </div>
      </div>

      <div class="userperms-modules">
       <transition-group name="userperms-fade" tag="div" class="userperms-modules-group">
       <article v-for="mod in filteredModules" :key="mod.code"
        class="userperms-module"
        :class="{ 'is-off': !moduleActive(mod), 'is-collapsed': collapsed[mod.code] }">
        <header class="userperms-module-head">
         <button
          type="button" class="userperms-module-collapse"
          :aria-label="'Expandir o contraer ' + mod.name"
          @click="toggleCollapse(mod.code)">
          <v-icon aria-hidden="true">
           {{ collapsed[mod.code] ? 'chevron_right' : 'expand_more' }}
          </v-icon>
         </button>
         <span class="userperms-module-icon">
          <img v-if="mod.icon" :src="mod.icon" alt="" class="userperms-module-iconimg" @error="onIconError(mod)">
          <v-icon v-else aria-hidden="true">folder_open</v-icon>
         </span>
         <div class="userperms-module-title">
          <strong>{{ mod.name }}</strong>
          <div class="userperms-module-meter">
           <span class="userperms-module-meter-bar"><i :style="{ width: coverage(mod) + '%' }"></i></span>
           <span class="userperms-module-meter-text">
            {{ activeResources(mod) }}/{{ mod.resources.length }} con acceso
           </span>
           <span v-if="moduleOverrides(mod) > 0" class="userperms-badge-custom">
            {{ moduleOverrides(mod) }} personalizado(s)
           </span>
          </div>
         </div>
         <v-switch
          class="userperms-module-switch" color="success" hide-details :disabled="saving"
          :input-value="moduleActive(mod)"
          :aria-label="'Acceso al módulo ' + mod.name"
          @change="toggleModule(mod, $event)" />
        </header>

        <div v-show="!collapsed[mod.code]" class="userperms-reslist">
         <div v-for="res in mod.resources" :key="res.code"
          class="userperms-res"
          :class="{ 'is-off': !isOn(res.code, 'read'), 'is-custom': resourceOverrides(res) > 0 }">
          <div class="userperms-res-head">
           <span class="userperms-res-name">
            {{ res.name }}
            <span v-if="resourceOverrides(res) > 0" class="userperms-res-tag">
             <v-icon small aria-hidden="true">tune</v-icon>Personalizado
            </span>
           </span>
           <div class="userperms-res-right">
            <div class="userperms-res-crud">
             <button
              v-for="a in actionCols" :key="a.code" type="button"
              class="userperms-crud"
              :class="{ 'is-on': isOn(res.code, a.code), 'is-master': a.master, 'is-override': isOverridden(res.code, a.code) }"
              :title="overrideTitle(res.code, a)"
              :aria-pressed="isOn(res.code, a.code) ? 'true' : 'false'"
              :disabled="saving || (a.code !== 'read' && !isOn(res.code, 'read'))"
              @click="toggleAction(res.code, a.code, !isOn(res.code, a.code))">
              <v-icon small aria-hidden="true">
               {{ isOn(res.code, a.code) ? 'check_box' : 'check_box_outline_blank' }}
              </v-icon>
              <span>{{ a.label }}</span>
             </button>
            </div>
            <v-tooltip v-if="resourceOverrides(res) > 0" top>
             <v-btn
              slot="activator" icon class="userperms-res-reset"
              :disabled="saving" aria-label="Volver este recurso al estándar del rol"
              @click="resetResource(res)">
              <v-icon small aria-hidden="true">restart_alt</v-icon>
             </v-btn>
             <span>Volver este recurso al estándar del rol</span>
            </v-tooltip>
           </div>
          </div>

          <div v-if="res.buttons && res.buttons.length" class="userperms-res-buttons">
           <span class="userperms-res-buttons-label">
            <v-icon small aria-hidden="true">smart_button</v-icon>Acciones
           </span>
           <button
            v-for="btn in res.buttons" :key="btn.code" type="button"
            class="userperms-btn"
            :class="{ 'is-on': isOn(res.code, btn.code), 'is-override': isOverridden(res.code, btn.code) }"
            :aria-pressed="isOn(res.code, btn.code) ? 'true' : 'false'"
            :title="overrideTitleButton(res.code, btn)"
            :disabled="saving || !isOn(res.code, 'read')"
            @click="toggleAction(res.code, btn.code, !isOn(res.code, btn.code))">
            <v-icon small aria-hidden="true">
             {{ isOn(res.code, btn.code) ? 'check_box' : 'check_box_outline_blank' }}
            </v-icon>
            <span>{{ btn.name }}</span>
           </button>
          </div>
         </div>
        </div>
       </article>
       </transition-group>
      </div>

      <div class="userperms-footer">
       <div class="userperms-summary" role="status" aria-live="polite">
        <span class="userperms-summary-icon"><v-icon aria-hidden="true">tune</v-icon></span>
        <div>
         <strong>{{ totalOverrides }} {{ totalOverrides === 1 ? 'personalización' : 'personalizaciones' }}</strong>
         <span>{{ dirty ? 'Tienes cambios sin guardar.' : 'Sin cambios pendientes.' }}</span>
        </div>
       </div>
       <app-button
        green text="Guardar personalización" width="220" height="44"
        :loading="saving" :disabled="saving || !dirty"
        @click="askSave" />
      </div>
     </template>

     <v-btn v-if="!loading && !ready" flat @click="load">Reintentar</v-btn>
    </v-card-text>
   </v-card>

   <v-dialog v-model="confirmOpen" persistent max-width="460">
    <v-card class="userperms-confirm">
     <div class="userperms-confirm-head">
      <span class="userperms-confirm-icon"><v-icon aria-hidden="true">manage_accounts</v-icon></span>
      <div>
       <strong>¿Guardar personalización?</strong>
       <span>Se aplican los ajustes de <b>{{ target.userName }}</b> sobre el rol <b>{{ roleName }}</b>.</span>
      </div>
     </div>
     <div class="userperms-confirm-body">
      <div class="userperms-confirm-stats">
       <div class="userperms-confirm-stat is-allow">
        <strong>+{{ overrideSummary.allow }}</strong><span>agregados al estándar</span>
       </div>
       <div class="userperms-confirm-stat is-deny">
        <strong>−{{ overrideSummary.deny }}</strong><span>quitados del estándar</span>
       </div>
      </div>
      <p v-if="totalOverrides === 0" class="userperms-confirm-empty">
       <v-icon small aria-hidden="true">info</v-icon>
       Sin personalizaciones: el usuario queda igual al estándar del rol.
      </p>
     </div>
     <v-card-actions class="userperms-confirm-actions">
      <v-btn flat :disabled="saving" @click="confirmOpen = false">Cancelar</v-btn>
      <v-spacer />
      <app-button
       green text="Sí, guardar" width="150" height="40"
       :loading="saving" @click="confirmSave" />
     </v-card-actions>
    </v-card>
   </v-dialog>
  </div>
 </section>
</template>

<script>
// Ajusta la ruta a tu componente global de boton:
import AppButton from '@/components/shared/app-button';
import {
	ACTIONS, ACTION_ORDER, normalizeSidebar, toOverrides, toResourcesMap, fromResourcesMap,
	buildNameMap, applyNameMap, applyMenuOrder,
} from '@/shared/permissions-catalog';

// Codes de rol con acceso total por defecto (estándar completo).
// 'ACCOUNTANT' = CONTADOR. Su "estándar" es TODO (ver/crear/editar/eliminar + botones),
// sin depender de lo que mande el ACL. Si el usuario tiene personalización guardada,
// esa se respeta (solo cambia el "estándar" al que vuelve "Volver al estándar").
const FULL_ACCESS_ROLES = ['ACCOUNTANT'];

export default {
	name: 'page-user-permissions',
	components: { AppButton },
	data() {
		return {
			allowed: false,
			loading: false,
			saving: false,
			ready: false,
			error: '',
			message: '',
			target: {},
			roleId: null,
			roleCode: null,
			roleName: '',
			modules: [],
			actionCols: ACTION_ORDER.map(code => ACTIONS[code]),
			baseMap: {},
			permMap: {},
			savedSnapshot: '',
			codeCompany: null,
			codeProject: null,
			codeApp: null,
			codeUser: null,
			filterText: '',
			collapsed: {},
			confirmOpen: false,
		};
	},
	created() { this.load(); },
	computed: {
		overrides() {
			return toOverrides(this.modules, this.baseMap, this.permMap);
		},
		totalOverrides() {
			return this.overrides.length;
		},
		dirty() {
			return JSON.stringify(toResourcesMap(this.modules, this.permMap)) !== this.savedSnapshot;
		},
		overrideSummary() {
			let allow = 0;
			let deny = 0;
			this.overrides.forEach((o) => {
				if (o.effect === 'deny') deny += 1;
				else allow += 1;
			});
			return { allow, deny };
		},
		allCollapsed() {
			return this.modules.length > 0
				&& this.modules.every(m => this.collapsed[m.code]);
		},
		filteredModules() {
			const q = (this.filterText || '').trim().toLowerCase();
			if (!q) return this.modules;
			return this.modules
				.map((mod) => {
					if (mod.name && mod.name.toLowerCase().includes(q)) return mod;
					const resources = mod.resources
						.filter(res => res.name && res.name.toLowerCase().includes(q));
					return { ...mod, resources };
				})
				.filter(mod => mod.resources.length);
		},
	},
	methods: {
		onIconError(mod) { this.$set(mod, 'icon', ''); },
		isOn(resourceCode, actionCode) {
			const set = this.permMap[resourceCode];
			return !!(set && set.has(actionCode));
		},
		isOverridden(resourceCode, actionCode) {
			const base = this.baseMap[resourceCode];
			const inBase = !!(base && base.has(actionCode));
			return inBase !== this.isOn(resourceCode, actionCode);
		},
		overrideTitle(resourceCode, action) {
			if (!this.isOverridden(resourceCode, action.code)) return action.label;
			return this.isOn(resourceCode, action.code)
				? `${action.label}: agregado (fuera del estándar del rol)`
				: `${action.label}: quitado (el rol sí lo tiene)`;
		},
		overrideTitleButton(resourceCode, btn) {
			if (!this.isOverridden(resourceCode, btn.code)) return btn.name;
			return this.isOn(resourceCode, btn.code)
				? `${btn.name}: agregado (fuera del estándar del rol)`
				: `${btn.name}: quitado (el rol sí lo tiene)`;
		},
		moduleActive(mod) {
			return mod.resources.some(res => this.isOn(res.code, 'read'));
		},
		activeResources(mod) {
			return mod.resources.filter(res => this.isOn(res.code, 'read')).length;
		},
		coverage(mod) {
			if (!mod.resources.length) return 0;
			return Math.round((this.activeResources(mod) / mod.resources.length) * 100);
		},
		resourceOverrides(res) {
			const base = this.baseMap[res.code] || new Set();
			const eff = this.permMap[res.code] || new Set();
			const codes = new Set([
				...ACTION_ORDER,
				...(res.buttons || []).map(b => b.code),
				...base,
				...eff,
			]);
			let n = 0;
			codes.forEach((a) => { if (base.has(a) !== eff.has(a)) n += 1; });
			return n;
		},
		moduleOverrides(mod) {
			return mod.resources.reduce((acc, res) => acc + this.resourceOverrides(res), 0);
		},
		toggleCollapse(code) {
			this.$set(this.collapsed, code, !this.collapsed[code]);
		},
		toggleAllCollapse() {
			const anyOpen = this.modules.some(m => !this.collapsed[m.code]);
			const map = {};
			this.modules.forEach((m) => { map[m.code] = anyOpen; });
			this.collapsed = map;
		},
		toggleAction(resourceCode, actionCode, value) {
			const set = new Set(this.permMap[resourceCode] || []);
			if (actionCode === 'read') {
				if (value) set.add('read');
				else set.clear();
			} else if (value) {
				set.add(actionCode);
				set.add('read');
			} else {
				set.delete(actionCode);
			}
			this.$set(this.permMap, resourceCode, set);
		},
		toggleModule(mod, value) {
			mod.resources.forEach((res) => { this.toggleAction(res.code, 'read', value); });
		},
		resetResource(res) {
			const base = this.baseMap[res.code] || new Set();
			this.$set(this.permMap, res.code, new Set(base));
		},
		resetAll() {
			const map = {};
			Object.keys(this.baseMap).forEach((rc) => { map[rc] = new Set(this.baseMap[rc]); });
			this.permMap = map;
		},
		// ¿El rol del usuario es de acceso total (CONTADOR / ACCOUNTANT)?
		// Se valida por code y, como respaldo, por nombre que contenga 'CONTADOR'.
		isFullAccessRole() {
			const code = (this.roleCode || '').toUpperCase();
			const name = (this.roleName || '').toUpperCase();
			return FULL_ACCESS_ROLES.indexOf(code) > -1 || name.indexOf('CONTADOR') > -1;
		},
		// Estándar COMPLETO: por cada recurso, TODAS las acciones CRUD + todos los botones.
		buildFullBase() {
			const map = {};
			this.modules.forEach((mod) => {
				mod.resources.forEach((res) => {
					const set = new Set(ACTION_ORDER); // read, create, update, delete
					(res.buttons || []).forEach(btn => set.add(btn.code));
					map[res.code] = set;
				});
			});
			return map;
		},
		buildDefaultBase() {
			// El ACL (sidebar) no define CRUD: solo indica qué recursos ve el usuario.
			// Default cuando el rol aún no tiene estándar: Ver + todos los botones.
			const map = {};
			this.modules.forEach((mod) => {
				mod.resources.forEach((res) => {
					const set = new Set(['read']);
					(res.buttons || []).forEach(btn => set.add(btn.code));
					map[res.code] = set;
				});
			});
			return map;
		},
		cloneMap(src) {
			const map = {};
			Object.keys(src || {}).forEach((rc) => { map[rc] = new Set(src[rc]); });
			return map;
		},
		report(errorOrResponse) {
			const response = (errorOrResponse && errorOrResponse.response) || errorOrResponse || {};
			this.error = (response.data && response.data.message)
				|| 'No se pudo completar la operación. Intenta nuevamente.';
		},
		async loadButtons() {
			const appCode = process.env.APP_CODE;
			const params = { languages: 'es' };
			const headers = { Accept: 'application/vnd.apiacl.v2+json' };
			const resources = [];
			this.modules.forEach((mod) => {
				mod.resources.forEach((res) => { resources.push(res); });
			});
			await Promise.all(resources.map(async (res) => {
				try {
					const { data } = await this.$httpAcl.get(
						`sidebar/${appCode}/resource/${res.code}`,
						{ params, headers },
					);
					const list = (data && data.data) || [];
					const buttons = list
						.filter(a => a.code && a.code !== 'HELP')
						.map(a => ({ code: a.code, name: a.languages || a.name || a.code }));
					this.$set(res, 'buttons', buttons);
				} catch (err) {
					this.$set(res, 'buttons', res.buttons || []);
				}
			}));
		},
		async load() {
			this.loading = true; this.ready = false; this.allowed = false;
			this.error = ''; this.message = '';
			try {
				const { data: session } = await this.$http.get('company-token');
				this.allowed = !!(session.auxiliary
					&& [true, 1].includes(session.auxiliary.flagAccountant));
				if (!this.allowed) {
					this.error = 'Solo el contador administrador puede configurar permisos.';
					return;
				}
				this.codeCompany = session.companyAcl && session.companyAcl.code_company;
				// Ajusta si tu sesión expone el proyecto en otro campo:
				this.codeProject = (session.companyAcl && session.companyAcl.code_project)
					|| process.env.PROJECT_CODE || 'maki_project';
				this.codeApp = process.env.APP_CODE;
				if (!this.codeCompany) { this.error = 'No se pudo determinar la organización de la sesión.'; return; }

				// Resolvemos el usuario por id → codeUser + nombre (para el ACL y la cabecera)
				const id = Number(this.$route.params.id);
				const { data: aux } = await this.$http.get(`auxiliary-details/${id}`);
				if (!aux || !aux.id) { this.error = 'Usuario no encontrado.'; return; }
				this.codeUser = this.$route.params.codeUser || aux.codeUser || aux.code_user
					|| aux.userCode || aux.codeUserAcl || aux.email;
				this.target = { userName: aux.userName, email: aux.email, codeUser: this.codeUser };
				if (!this.codeUser) { this.error = 'El usuario no tiene codeUser en ACL.'; return; }

				// Roles + personalización del usuario (para obtener su roleId)
				const [rolesRes, ovrRes] = await Promise.all([
					this.$httpAcl.get(`company/${this.codeCompany}/role`),
					this.$httpAcl.get(
						`project/${this.codeProject}/user/${this.codeUser}/resource-permission`,
						{ params: { codeCompany: this.codeCompany, codeApp: this.codeApp } },
					),
				]);

				const ovrData = (ovrRes.data && ovrRes.data.data) || {};
				this.roleId = ovrData.roleId || null;
				if (!this.roleId) {
					this.error = 'El usuario no tiene un rol asignado. Asígnalo antes de personalizar.';
					return;
				}

				// Sidebar FILTRADO por el rol del usuario (estructura + qué recursos tiene)
				// + Menú REAL (sidebar/{APP_CODE}) solo para tomar los nombres visibles,
				//   para que esta pantalla muestre EXACTAMENTE lo mismo que la barra lateral.
				const [sidebarRes, menuRes] = await Promise.all([
					this.$httpAcl.get(`app/${this.codeApp}/role/${this.roleId}/sidebar`),
					this.$httpAcl.get(`sidebar/${process.env.APP_CODE}`),
				]);
				const menuData = menuRes.data.data;
				const nameMap = buildNameMap(menuData);
				// Nombres del menú real + mismo orden que el menú (por position).
				this.modules = applyMenuOrder(
					applyNameMap(normalizeSidebar(sidebarRes.data.data), nameMap),
					menuData,
				);
				await this.loadButtons();

				const rolesData = rolesRes.data;
				const roles = Array.isArray(rolesData) ? rolesData : (rolesData.data || []);
				const role = roles.find(r => r.id === this.roleId);
				this.roleName = (role && role.name) || 'Rol del usuario';
				this.roleCode = (role && (role.code || role.roleCode)) || null;

				// Estándar del rol (base)
				const { data: baseData } = await this.$httpAcl.get(
					`company/${this.codeCompany}/role/${this.roleId}/permission`,
				);
				const basePermission = (baseData && baseData.data && baseData.data.permission) || {};
				const baseResources = (basePermission && basePermission.resources) || {};
				// Rol de acceso total (CONTADOR / ACCOUNTANT): el estándar es TODO
				// (ver/crear/editar/eliminar + botones), sin depender del ACL. Así
				// "Volver al estándar" deja todo marcado.
				if (this.isFullAccessRole()) {
					this.baseMap = this.buildFullBase();
				} else {
					// Si el rol aún no tiene estándar guardado, usamos el default del ACL
					// (lo que trae el sidebar): Ver + todos los botones por recurso.
					this.baseMap = Object.keys(baseResources).length
						? fromResourcesMap(baseResources)
						: this.buildDefaultBase();
				}

				// Efectivo del usuario: lo guardado, o el estándar si no ha personalizado
				const userPermission = ovrData.permission;
				this.permMap = (userPermission && userPermission.resources)
					? fromResourcesMap(userPermission.resources)
					: this.cloneMap(this.baseMap);
				this.savedSnapshot = JSON.stringify(toResourcesMap(this.modules, this.permMap));
				this.ready = true;
			} catch (error) { this.report(error); } finally { this.loading = false; }
		},
		askSave() {
			if (!this.ready || !this.dirty || this.saving) return;
			this.confirmOpen = true;
		},
		confirmSave() {
			this.confirmOpen = false;
			this.savePermissions();
		},
		async savePermissions() {
			if (this.saving || !this.ready || !this.dirty) return;
			this.saving = true; this.error = ''; this.message = '';
			try {
				const payload = {
					codeApp: this.codeApp,
					codeUser: this.codeUser,
					codeCompany: this.codeCompany,
					permission: { resources: toResourcesMap(this.modules, this.permMap) },
				};
				await this.$httpAcl.patch('user-resource-permission', payload);
				this.savedSnapshot = JSON.stringify(payload.permission.resources);
				const s = this.overrideSummary;
				this.message = `Personalización de "${this.target.userName}" guardada: `
					+ `${s.allow} agregada(s) y ${s.deny} quitada(s) respecto al rol ${this.roleName}.`;
				// Recargamos para sincronizar menú y permisos con lo recién guardado.
				setTimeout(() => { window.location.reload(); }, 1200);
			} catch (error) { this.report(error); } finally { this.saving = false; }
		},
	},
};
</script>

<style lang="scss">
.userperms-page {
	background: color(background-table);
	min-height: calc(100vh - 80px);
	color: color(text);
	font-family: font(AvenirRegular);
}
.userperms-content { max-width: 1760px; padding: 28px 32px; margin: auto; }
.userperms-card { position: relative; border: 1px solid color(grey-divider); border-radius: 12px; overflow: hidden; }
.userperms-heading { gap: 12px; padding: 24px; border-bottom: 1px solid color(grey-divider); }
.userperms-heading h1 { background: transparent !important; color: color(black); font-family: font(AvenirBold); font-size: 23px; margin: 4px 0 0; padding: 0; }
.userperms-eyebrow { color: color(secondary); font-size: 11px; letter-spacing: 1.2px; }
.userperms-title-group { display: flex; align-items: center; gap: 16px; }
.userperms-title-icon { display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 14px; background: #e8f5f6; }
.userperms-page .userperms-title-icon .v-icon { color: #008e95 !important; font-size: 28px; }
.userperms-sub { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.userperms-sub span { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }
.userperms-chip-role { background: #edf1f5; color: #526578; }
.userperms-chip-custom { background: #fdf2e2; color: #b9791a; }
.userperms-chip-standard { background: #e0f3ec; color: #087c75; }
.userperms-page .userperms-sub .v-icon { color: inherit !important; font-size: 15px !important; }
.userperms-card .v-card__text { padding: 24px; }
.userperms-card .v-btn { border-radius: 6px; text-transform: none; letter-spacing: normal; }
.userperms-page .userperms-back-action { height: 38px; background: #fff !important; border-color: #c6d8e7; box-shadow: none; }
.userperms-page .userperms-back-action:not(.v-btn--disabled), .userperms-page .userperms-back-action:not(.v-btn--disabled) .v-icon { color: #326282 !important; }
.userperms-intro { font-size: 13px; line-height: 1.6; margin: 0 0 18px; color: #4a5a63; }
.userperms-tag-inline { display: inline-flex; align-items: center; padding: 1px 7px; border-radius: 999px; background: #fdf2e2; color: #b9791a; font-size: 12px; font-weight: 600; }

.userperms-toolbar { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 18px; }
.userperms-search { flex: 1 1 240px; min-width: 200px; max-width: 460px; }
.userperms-bulk { display: flex; gap: 8px; margin-left: auto; }

.userperms-modules-group { column-count: 2; column-gap: 14px; }
.userperms-module { border: 1px solid #dce6eb; border-radius: 12px; overflow: hidden; background: #fff; transition: box-shadow .2s ease, border-color .2s ease; break-inside: avoid; -webkit-column-break-inside: avoid; page-break-inside: avoid; margin-bottom: 14px; }
.userperms-module:hover { border-color: #bcd5db; box-shadow: 0 6px 18px rgba(10, 60, 70, 0.07); }
.userperms-module.is-off { background: #fafbfc; }
.userperms-module-head { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #f4f8fa; border-bottom: 1px solid #e3edf1; }
.userperms-module-collapse { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; border: 0; border-radius: 8px; background: transparent; cursor: pointer; transition: background .15s ease; }
.userperms-module-collapse:hover { background: #e3edf1; }
.userperms-page .userperms-module-collapse .v-icon { color: #6b7a84 !important; }
.userperms-module-icon { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 38px; height: 38px; border-radius: 10px; background: #0a9ca2; }
.userperms-module-iconimg { width: 18px; height: 18px; object-fit: contain; filter: brightness(0) invert(1); opacity: .95; }
.userperms-page .userperms-module-icon .v-icon { color: #fff !important; font-size: 20px; }
.userperms-module.is-off .userperms-module-icon { background: #aeb9c1; }
.userperms-module-title { flex: 1; min-width: 0; }
.userperms-module-title strong { display: block; color: #22323b; font-size: 14px; font-weight: 700; }
.userperms-module-meter { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 5px; }
.userperms-module-meter-bar { position: relative; flex: 0 0 90px; height: 5px; border-radius: 999px; background: #e3edf1; overflow: hidden; }
.userperms-module-meter-bar i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 999px; background: linear-gradient(90deg, #16a085, #0a9ca2); transition: width .35s ease; }
.userperms-module.is-off .userperms-module-meter-bar i { background: #c3ccd3; }
.userperms-module-meter-text { font-size: 11px; color: #6b7a84; }
.userperms-badge-custom { font-size: 11px; font-weight: 600; color: #b9791a; background: #fdf2e2; border-radius: 999px; padding: 1px 8px; }
.userperms-module-switch { flex: 0 0 auto; margin: 0; }

.userperms-reslist { display: flex; flex-direction: column; }
.userperms-res { padding: 12px 16px; border-bottom: 1px solid #f0f3f5; transition: background .15s ease; }
.userperms-res:last-child { border-bottom: 0; }
.userperms-res:hover { background: #f7fbfb; }
.userperms-res.is-off { background: #fbfcfd; }
.userperms-res.is-custom { box-shadow: inset 3px 0 0 0 #f0b44a; }
.userperms-res-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.userperms-res-name { font-size: 13px; font-weight: 600; color: #35474f; display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.userperms-res.is-off .userperms-res-name { color: #9aa6b0; }
.userperms-res-tag { display: inline-flex; align-items: center; gap: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; color: #b9791a; background: #fdf2e2; border-radius: 999px; padding: 2px 7px; }
.userperms-page .userperms-res-tag .v-icon { color: #b9791a !important; font-size: 13px !important; }
.userperms-res-right { display: flex; align-items: center; gap: 6px; }
.userperms-res-crud { display: flex; flex-wrap: wrap; gap: 8px; }
.userperms-crud { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border: 1px solid #e3eaee; border-radius: 8px; background: #fff; cursor: pointer; font: inherit; line-height: 1; transition: all .15s ease; }
.userperms-crud span { font-size: 12px; font-weight: 600; color: #7b8a94; white-space: nowrap; }
.userperms-crud span b { font-weight: 800; }
.userperms-page .userperms-crud .v-icon { color: #b4c0c9 !important; font-size: 18px !important; }
.userperms-crud.is-on { border-color: #bfe6cf; background: #eefaf1; }
.userperms-crud.is-on span { color: #0b6f68; }
.userperms-page .userperms-crud.is-on .v-icon { color: #16a085 !important; }
.userperms-crud.is-master.is-on { border-color: #16a085; box-shadow: inset 0 0 0 1px #c4ecd9; }
.userperms-crud.is-override { border-color: #f0b44a !important; background: #fff8ec; box-shadow: 0 0 0 1px #f6cf8a; }
.userperms-crud.is-override span { color: #a9741c; }
.userperms-crud:disabled { cursor: not-allowed; opacity: .45; }
.userperms-crud:not(:disabled):hover { transform: translateY(-1px); }
.userperms-page .userperms-res-reset { width: 30px; height: 30px; margin: 0; border: 1px solid #f0d6a6; border-radius: 8px; background: #fdf7ec !important; }
.userperms-page .userperms-res-reset .v-icon { color: #b9791a !important; }

.userperms-res-buttons { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #e7edf1; }
.userperms-res-buttons-label { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: #8a98a2; text-transform: uppercase; letter-spacing: .3px; margin-right: 4px; }
.userperms-page .userperms-res-buttons-label .v-icon { color: #8a98a2 !important; font-size: 15px; }
.userperms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 12px 4px 8px; border: 1px solid #d7e2ea; border-radius: 999px; cursor: pointer; background: #fff; font: inherit; line-height: 1; transition: all .15s ease; }
.userperms-btn span { font-size: 12px; color: #4a5a63; }
.userperms-page .userperms-btn .v-icon { color: #b4c0c9 !important; font-size: 17px !important; }
.userperms-btn.is-on { border-color: #9fd9c3; background: #eafaf2; }
.userperms-btn.is-on span { color: #0b6f68; font-weight: 600; }
.userperms-page .userperms-btn.is-on .v-icon { color: #16a085 !important; }
.userperms-btn.is-override { border-color: #f0b44a !important; background: #fff8ec; box-shadow: 0 0 0 1px #f6cf8a; }
.userperms-btn.is-override span { color: #a9741c; }
.userperms-btn:disabled { cursor: not-allowed; opacity: .5; }
.userperms-btn:not(:disabled):hover { transform: translateY(-1px); }

.userperms-footer { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-top: 20px; padding: 18px 20px; border: 1px solid #dce6eb; border-radius: 10px; background: #f8fafc; position: sticky; bottom: 12px; z-index: 3; box-shadow: 0 6px 20px rgba(20, 50, 60, 0.08); }
.userperms-summary { display: flex; align-items: center; gap: 12px; }
.userperms-summary strong { display: block; color: #2c404a; font-size: 14px; }
.userperms-summary > div > span { display: block; margin-top: 4px; color: #607582; font-size: 12px; }
.userperms-summary-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 40px; height: 40px; border-radius: 10px; background: #fdf2e2; }
.userperms-page .userperms-summary-icon .v-icon { color: #b9791a !important; }

.userperms-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 20; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; background: rgba(255, 255, 255, 0.76); }
.userperms-overlay span { font-size: 13px; font-weight: 600; color: #35474f; }

.userperms-confirm { border-radius: 14px !important; overflow: hidden; }
.userperms-confirm-head { display: flex; gap: 14px; padding: 22px 22px 8px; }
.userperms-confirm-icon { flex: 0 0 44px; height: 44px; border-radius: 12px; background: #e8f5f6; display: flex; align-items: center; justify-content: center; }
.userperms-confirm-icon .v-icon { color: #008e95 !important; font-size: 24px; }
.userperms-confirm-head strong { display: block; font-size: 16px; color: #22323b; }
.userperms-confirm-head > div > span { display: block; margin-top: 4px; font-size: 13px; line-height: 1.5; color: #6b7a84; }
.userperms-confirm-body { padding: 10px 22px 4px; }
.userperms-confirm-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.userperms-confirm-stat { text-align: center; padding: 12px 8px; border: 1px solid #e3edf1; border-radius: 10px; background: #f8fafc; }
.userperms-confirm-stat strong { display: block; font-size: 22px; font-weight: 700; }
.userperms-confirm-stat span { font-size: 11px; color: #6b7a84; }
.userperms-confirm-stat.is-allow strong { color: #0b6f68; }
.userperms-confirm-stat.is-deny strong { color: #b23b2e; }
.userperms-confirm-empty { display: flex; align-items: center; gap: 6px; margin: 14px 0 4px; font-size: 13px; color: #7b8a94; }
.userperms-confirm-empty .v-icon { color: #c08a2b !important; }
.userperms-confirm-actions { padding: 12px 18px 16px; }

.userperms-fade-enter-active, .userperms-fade-leave-active { transition: opacity .25s ease, transform .25s ease; }
.userperms-fade-enter, .userperms-fade-leave-to { opacity: 0; transform: translateY(-4px); }

@media (max-width: 1180px) {
	.userperms-modules-group { column-count: 1; }
}
@media (max-width: 768px) {
	.userperms-content { padding: 16px 12px; }
	.userperms-heading { padding: 16px; }
	.userperms-card .v-card__text { padding: 16px; }
	.userperms-search { width: 100%; }
	.userperms-footer { flex-direction: column; align-items: stretch; }
}
</style>



// WEBPACK FOOTER //
// src/pages/users/user-permissions.vue